import {
  adminInput,
  adminButtonPrimary,
  adminSectionTitle,
  adminModalPanel,
} from '../../layout/adminStyles';
import React, { FC, FormEvent, useCallback, useEffect, useRef } from 'react';
import { SystemAdminStoreForm, STORE_GRADE_OPTIONS } from './storeForm';
import { useAutoFurigana } from './useAutoFurigana';

type Props = {
  isShowModal: boolean;
  form: SystemAdminStoreForm;
  isSaving: boolean;
  onChange: (form: SystemAdminStoreForm) => void;
  onClose: () => void;
  onSubmit: () => void;
};

const labelClass = 'mb-1 block text-sm font-medium text-stone-700';

const StoreCreateModal: FC<Props> = ({
  isShowModal,
  form,
  isSaving,
  onChange,
  onClose,
  onSubmit,
}) => {
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const handleFuriganaAuto = useCallback(
    (furigana: string) => {
      onChange({ ...formRef.current, furigana });
    },
    [onChange]
  );

  const { nameRef, markFuriganaTouched } = useAutoFurigana(
    form.name,
    form.furigana,
    handleFuriganaAuto,
    isShowModal
  );

  if (!isShowModal) {
    return null;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const handleEmailChange = (email: string) => {
    const nextForm = { ...form, email };
    if (!form.login_id.trim() && email.includes('@')) {
      nextForm.login_id = email.split('@')[0];
    }
    onChange(nextForm);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="store-create-modal-title"
    >
      <div className={adminModalPanel}>
        <div className="mb-4 flex items-center justify-between border-b border-stone-200 pb-3">
          <h2 id="store-create-modal-title" className={`${adminSectionTitle} mb-0 border-0 pb-0`}>
            新規店舗登録
          </h2>
          <button
            type="button"
            className="text-2xl leading-none text-stone-500 hover:text-stone-800"
            onClick={onClose}
            aria-label="閉じる"
            disabled={isSaving}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="create-store-name" className={labelClass}>
              店舗名
            </label>
            <input
              id="create-store-name"
              ref={nameRef}
              type="text"
              required
              className={`${adminInput} w-full`}
              value={form.name}
              onChange={(e) => onChange({ ...form, name: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="create-store-furigana" className={labelClass}>
              ふりがな
            </label>
            <input
              id="create-store-furigana"
              type="text"
              required
              className={`${adminInput} w-full`}
              value={form.furigana}
              onChange={(e) => {
                markFuriganaTouched();
                onChange({ ...form, furigana: e.target.value });
              }}
            />
          </div>

          <div>
            <label htmlFor="create-store-grade" className={labelClass}>
              登録プラン
            </label>
            <select
              id="create-store-grade"
              required
              className={`${adminInput} w-full`}
              value={form.grade}
              onChange={(e) => onChange({ ...form, grade: e.target.value })}
            >
              {STORE_GRADE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-zip" className={labelClass}>
                郵便番号
              </label>
              <input
                id="create-store-zip"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.zip}
                onChange={(e) => onChange({ ...form, zip: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="create-store-pref" className={labelClass}>
                都道府県
              </label>
              <input
                id="create-store-pref"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.pref}
                onChange={(e) => onChange({ ...form, pref: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-city" className={labelClass}>
                市区町村
              </label>
              <input
                id="create-store-city"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.city}
                onChange={(e) => onChange({ ...form, city: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="create-store-address" className={labelClass}>
                住所
              </label>
              <input
                id="create-store-address"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.address}
                onChange={(e) => onChange({ ...form, address: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label htmlFor="create-store-building" className={labelClass}>
              建物名（任意）
            </label>
            <input
              id="create-store-building"
              type="text"
              className={`${adminInput} w-full`}
              value={form.building}
              onChange={(e) => onChange({ ...form, building: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-latitude" className={labelClass}>
                緯度
              </label>
              <input
                id="create-store-latitude"
                type="number"
                step="any"
                required
                className={`${adminInput} w-full`}
                value={form.latitude}
                onChange={(e) => onChange({ ...form, latitude: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="create-store-longitude" className={labelClass}>
                経度
              </label>
              <input
                id="create-store-longitude"
                type="number"
                step="any"
                required
                className={`${adminInput} w-full`}
                value={form.longitude}
                onChange={(e) => onChange({ ...form, longitude: e.target.value })}
              />
            </div>
          </div>

          <p className="pt-2 text-sm font-medium text-stone-700">請求先情報</p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-billing-zip" className={labelClass}>
                請求先郵便番号
              </label>
              <input
                id="create-store-billing-zip"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.billing_zip}
                onChange={(e) => onChange({ ...form, billing_zip: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="create-store-billing-pref" className={labelClass}>
                請求先都道府県
              </label>
              <input
                id="create-store-billing-pref"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.billing_pref}
                onChange={(e) => onChange({ ...form, billing_pref: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-billing-city" className={labelClass}>
                請求先市区町村
              </label>
              <input
                id="create-store-billing-city"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.billing_city}
                onChange={(e) => onChange({ ...form, billing_city: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="create-store-billing-address" className={labelClass}>
                請求先住所
              </label>
              <input
                id="create-store-billing-address"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.billing_address}
                onChange={(e) => onChange({ ...form, billing_address: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label htmlFor="create-store-billing-building" className={labelClass}>
              請求先建物名（任意）
            </label>
            <input
              id="create-store-billing-building"
              type="text"
              className={`${adminInput} w-full`}
              value={form.billing_building}
              onChange={(e) => onChange({ ...form, billing_building: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-billing-contact-name" className={labelClass}>
                請求先担当者
              </label>
              <input
                id="create-store-billing-contact-name"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.billing_contact_name}
                onChange={(e) => onChange({ ...form, billing_contact_name: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="create-store-billing-email" className={labelClass}>
                請求先メールアドレス
              </label>
              <input
                id="create-store-billing-email"
                type="email"
                required
                className={`${adminInput} w-full`}
                value={form.billing_email}
                onChange={(e) => onChange({ ...form, billing_email: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-phone" className={labelClass}>
                電話番号
              </label>
              <input
                id="create-store-phone"
                type="tel"
                required
                className={`${adminInput} w-full`}
                value={form.phone_number}
                onChange={(e) => onChange({ ...form, phone_number: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="create-store-email" className={labelClass}>
                メールアドレス
              </label>
              <input
                id="create-store-email"
                type="email"
                required
                className={`${adminInput} w-full`}
                value={form.email}
                onChange={(e) => handleEmailChange(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-login-id" className={labelClass}>
                ログインID
              </label>
              <input
                id="create-store-login-id"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.login_id}
                onChange={(e) => onChange({ ...form, login_id: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="create-store-password" className={labelClass}>
                パスワード
              </label>
              <input
                id="create-store-password"
                type="password"
                required
                minLength={4}
                className={`${adminInput} w-full`}
                value={form.password}
                onChange={(e) => onChange({ ...form, password: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="create-store-open-time" className={labelClass}>
                開店時間
              </label>
              <input
                id="create-store-open-time"
                type="time"
                required
                className={`${adminInput} w-full`}
                value={form.open_time}
                onChange={(e) => onChange({ ...form, open_time: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="create-store-close-time" className={labelClass}>
                閉店時間
              </label>
              <input
                id="create-store-close-time"
                type="time"
                required
                className={`${adminInput} w-full`}
                value={form.close_time}
                onChange={(e) => onChange({ ...form, close_time: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-stone-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-stone-300 text-teal-500 focus:ring-teal-300"
                checked={form.has_parking}
                onChange={(e) => onChange({ ...form, has_parking: e.target.checked })}
              />
              駐車場あり
            </label>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-stone-200 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-xl border border-stone-300 px-5 py-2 text-stone-700 hover:bg-stone-50"
              onClick={onClose}
              disabled={isSaving}
            >
              キャンセル
            </button>
            <button type="submit" className={adminButtonPrimary} disabled={isSaving}>
              {isSaving ? '登録中...' : '登録'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoreCreateModal;
